import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  styled,
  Container,
  Link as MUILink,
} from '@mui/material';
import { toast } from 'react-toastify';
import LandingPageNav from '../../components/landingpage/LandingPageNav';
import { ProfileHeroSkeleton } from '../../components/skeletons/PageSkeletons';
import MortgageCalculator from '../../components/calculator/Calculator';
import MortgageCalculatorModern from '../../components/calculator/MortgageCalculatorModern';
import { apigetPublic, apidelete } from '../../service/api';
import Footer from '../../components/Footer/footer';
import LoanOfficerEditModal from '../LoanOfficer/Edit';

const ProfileContainer = styled(Container)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  marginTop: 8,
  paddingTop: '84px',
  position: 'relative',
  zIndex: 1,

  // ✅ prevents any accidental horizontal overflow
  overflowX: 'hidden',

  '@media print': {
    paddingTop: 0,
    marginTop: 0,
  },
}));

const TopContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  gap: theme.spacing(3),
  marginBottom: theme.spacing(4),
  flexWrap: 'wrap',

  // ✅ stack on mobile to avoid overflow
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'center',
  },
}));

const ProfileInfo = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(3),
  flex: 1,

  // ✅ IMPORTANT: allow shrinking on small screens
  minWidth: 0,

  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    textAlign: 'center',
    width: '100%',
  },
}));

const ProfileDetails = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),

  // ✅ keep long strings (like emails) from causing overflow
  minWidth: 0,
  '& a': {
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
  },
}));

const ActionButtons = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',

  // ✅ REMOVE negative margin + fixed minWidth (both cause side scroll)
  marginRight: 0,
  minWidth: 0,
  width: 'auto',

  [theme.breakpoints.down('sm')]: {
    width: '100%',
    alignItems: 'center',
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  margin: theme.spacing(1),
  padding: theme.spacing(2),
  fontSize: '1rem',
  textTransform: 'none',
  backgroundColor: '#b79a59 !important',
  color: '#fff !important',
  borderRadius: '8px',
  '&:hover': {
    backgroundColor: '#a5894f !important',
  },

  // ✅ mobile: buttons should not exceed screen width
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    maxWidth: 360,
  },
}));

const DescriptionContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

const CalculatorContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(4),
  '@media print': {
    marginTop: 0,
  },
}));

/**
 * @param {object} [props.initialOfficer] Officer record already fetched by the
 *   caller (e.g. SlugRouter). When supplied the page renders immediately
 *   instead of re-fetching the same record.
 */
function LoanOfficerProfile({ initialOfficer = null }) {
  const { slug } = useParams();
  const [officer, setOfficer] = useState(initialOfficer);
  const [loading, setLoading] = useState(!initialOfficer);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const navigate = useNavigate();

  const calculatorRef = useRef(null);

  const handleDelete = async () => {
    if (
      !window.confirm(
        'Are you sure you want to delete this Loan Officer? This action cannot be undone.'
      )
    ) {
      return;
    }

    try {
      const result = await apidelete(`loanofficer/${officer.slug}`);

      if (result?.status === 200) {
        toast.success('Loan Officer deleted successfully!');
        navigate('/dashboard/loanofficers');
      } else {
        toast.error(result?.data?.message || 'Failed to delete Loan Officer');
      }
    } catch (err) {
      console.error(err);
      toast.error('Unexpected error deleting Loan Officer.');
    }
  };

  const fetchOfficer = async () => {
    try {
      const result = await apigetPublic(`loanofficer/${slug}`);
      if (result?.status === 200) {
        setOfficer(result.data);
      }
    } catch (error) {
      console.error('Failed to fetch officer:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Skip the network round trip when the caller already handed us the record
    // for this slug; still refetch if the slug changes underneath us.
    if (initialOfficer && initialOfficer.slug === slug) return;
    fetchOfficer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const handleScrollToCalculator = () => {
    if (!calculatorRef.current) return;

    const navHeight = 90;

    const calculatorTop =
      calculatorRef.current.getBoundingClientRect().top +
      window.pageYOffset -
      navHeight;

    window.scrollTo({
      top: calculatorTop,
      behavior: 'smooth',
    });
  };

  if (loading) {
    return (
      <>
        <LandingPageNav hideApply />
        <Box sx={{ minHeight: '70vh', bgcolor: 'rgba(245,245,245,.85)' }}>
          <ProfileHeroSkeleton />
        </Box>
      </>
    );
  }

  if (!officer) return <div>Loan officer not found.</div>;

  const canEdit = user?.role === 'admin' || user?.role === 'marketing';

  const phoneHref = officer.phone
    ? `tel:${officer.phone.replace(/[^\d+]/g, '')}`
    : undefined;

  return (
    <>
      <Box className="mc-no-print">
        <LandingPageNav hideApply />
      </Box>

      <Box
        sx={{
          // ✅ additional safeguard (prevents any child from causing horizontal scroll)
          overflowX: 'hidden',

          backgroundImage:
            'url(https://res.cloudinary.com/ddhrdxc7d/image/upload/v1763995325/Background_Image_-_Blog_Pages_and_individual_LO_pages_1_w6e9ef.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(245,245,245,.85)',
            zIndex: 0,
          },
          '@media print': {
            backgroundImage: 'none !important',
            '&::before': {
              display: 'none',
            },
          },
        }}
      >
        {/* --- Role-Based Edit Button --- */}
        {canEdit && (
          <Box className="mc-no-print">
            <Button variant="outlined" onClick={() => setOpenEditDialog(true)}>
              Edit
            </Button>

            <LoanOfficerEditModal
              open={openEditDialog}
              onClose={() => setOpenEditDialog(false)}
              loanOfficer={officer}
              onUpdate={fetchOfficer}
            />

            <Button variant="outlined" color="error" onClick={handleDelete}>
              Delete
            </Button>
          </Box>
        )}

        <ProfileContainer>
          <TopContainer className="mc-no-print">
            <ProfileInfo>
              <img
                src={officer.imageUrl}
                alt={`${officer.firstName} ${officer.lastName}`}
                style={{
                  width: '220px',
                  height: '220px',
                  objectFit: 'cover',
                  borderRadius: '50%',
                  border: '4px solid rgba(0,0,0,0.05)',
                  boxShadow: '0 10px 24px rgba(0,0,0,0.12)',

                  // ✅ make sure image never forces overflow
                  maxWidth: '100%',
                }}
              />

              <ProfileDetails>
                <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                  {`${officer.firstName} ${officer.lastName}`}
                </Typography>

                {officer.title && (
                  <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                    {officer.title}
                  </Typography>
                )}

                {officer.nmlsId && (
                  <Typography variant="subtitle1" sx={{ color: 'text.secondary' }}>
                    NMLS #{officer.nmlsId}
                  </Typography>
                )}

                {officer.additionalLicensing &&
                  officer.additionalLicensing.trim() !== '' && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                        Serving
                      </Typography>

                      <Typography
                        variant="body1"
                        sx={{
                          whiteSpace: 'pre-line',
                          lineHeight: 1.6,
                          color: 'text.secondary',
                        }}
                      >
                        {officer.additionalLicensing}
                      </Typography>
                    </Box>
                  )}

                {/* Clickable Phone */}
                {officer.phone && (
                  <MUILink
                    underline="hover"
                    href={phoneHref}
                    sx={{
                      mt: 1,
                      fontSize: '1.125rem',
                      fontWeight: 600,
                      letterSpacing: 0.2,
                    }}
                    aria-label={`Call ${officer.firstName} ${officer.lastName}`}
                  >
                    {officer.phone}
                  </MUILink>
                )}

                {/* Clickable Email */}
                {officer.email && (
                  <MUILink
                    underline="hover"
                    href={`mailto:${officer.email}`}
                    sx={{
                      fontSize: '1.125rem',
                      fontWeight: 600,
                      letterSpacing: 0.2,
                    }}
                    aria-label={`Email ${officer.firstName} ${officer.lastName}`}
                  >
                    {officer.email}
                  </MUILink>
                )}

                {/* Google Reviews Button */}
{officer.googleReviewLink && (
  <Box sx={{ mt: 2 }}>
    <Button
      variant="contained"
      href={officer.googleReviewLink}
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        backgroundColor: '#b79a59',
        color: 'white',
        borderRadius: '30px',
        textTransform: 'none',
        px: 2,
        py: 1.2,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        boxShadow: 3,
        maxWidth: '100%',
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          bgcolor: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: '0 0 auto',
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 'bold',
            color: '#b79a59',
            fontSize: '1.1rem',
          }}
        >
          G
        </Typography>
      </Box>

      <Box textAlign="left" sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1 }}>
          Read My Google Reviews
        </Typography>
        <Box display="flex">
          {Array.from({ length: 5 }).map((_, i) => (
            <Typography key={i} sx={{ color: '#FFD700', fontSize: '1rem' }}>
              ★
            </Typography>
          ))}
        </Box>
      </Box>
    </Button>
  </Box>
)}

              </ProfileDetails>
            </ProfileInfo>

            <ActionButtons>
              {officer.applyLink && (
                <StyledButton variant="contained" href={officer.applyLink}>
                  Apply Now
                </StyledButton>
              )}

              <StyledButton variant="contained" onClick={handleScrollToCalculator}>
                Mortgage Calculator
              </StyledButton>
            </ActionButtons>
          </TopContainer>

          <DescriptionContainer className="mc-no-print">
            <Typography>{officer.bio}</Typography>
          </DescriptionContainer>

          {/* 👇 Attach the ref here */}
          <CalculatorContainer ref={calculatorRef}>
            <MortgageCalculatorModern logoSrc='/favicon/JET_singleline_color.jpg' />

          </CalculatorContainer>
        </ProfileContainer>
      </Box>

      <Box className="mc-no-print">
        <Footer />
      </Box>
    </>
  );
}

export default LoanOfficerProfile;
